import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, auth } from '../api/client';
import { Modal } from './Modal';
import type { ProfileDetails } from '../types/models';

/** One labelled row in the profile card. Hidden when the value is empty. */
function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <>
      <div className="profile-field-label">{label}</div>
      <div className="profile-field-value">{value}</div>
    </>
  );
}

function initialsOf(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || 'U';
}

/** The hall-ticket-style profile card shown in the My Profile popup. */
function ProfileCard({ profile, onPhoto, uploading }:
  { profile: ProfileDetails; onPhoto: (file: File) => void; uploading: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const idLabel = profile.role === 'Student' ? 'USN'
    : profile.role === 'Staff' ? 'Staff Code' : 'ID';
  const header = profile.institutionName || profile.organizationName;

  return (
    <div className="profile-card">
      {header && <div className="profile-inst">{header}</div>}

      <div className="profile-photo-wrap">
        {profile.photoDataUrl
          ? <img src={profile.photoDataUrl} alt="Profile photo" className="profile-photo" />
          : <div className="profile-photo profile-photo-initials" aria-hidden>{initialsOf(profile.fullName)}</div>}
        {profile.canUploadPhoto && (
          <>
            <button type="button" className="profile-photo-btn"
                    onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : (profile.photoDataUrl ? 'Change photo' : 'Add photo')}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                   style={{ display: 'none' }}
                   onChange={e => {
                     const f = e.target.files?.[0];
                     e.target.value = '';
                     if (f) onPhoto(f);
                   }} />
          </>
        )}
      </div>

      <div className="profile-grid">
        <Field label="Student" value={profile.role === 'Student' ? profile.fullName : undefined} />
        <Field label="Name" value={profile.role !== 'Student' ? profile.fullName : undefined} />
        <Field label={idLabel} value={profile.regNo} />
        <Field label="Department" value={profile.departmentName} />
        <Field label="Program" value={profile.programName} />
        <Field label="Batch" value={profile.batchName} />
        <Field label="Year / Sem" value={profile.academicYear} />
        <Field label="Designation" value={profile.staffRoleName} />
        <Field label="Email" value={profile.email} />
        <Field label="Mobile" value={profile.mobile} />
        <Field label="Gender" value={profile.gender} />
        <Field label="Father's Name" value={profile.fatherName} />
      </div>
    </div>
  );
}

/** Avatar + dropdown in the topbar: shows the signed-in user and offers My Profile / Change password / Sign out. */
export function UserMenu() {
  const nav = useNavigate();
  const user = auth.getUser();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileDetails | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Fetch the full profile when the popup opens.
  useEffect(() => {
    if (!profileOpen) return;
    setProfile(null);
    setProfileError(null);
    api.getMyProfile().then(setProfile).catch(e => setProfileError(e?.message ?? 'Failed to load profile.'));
  }, [profileOpen]);

  const name: string = user?.fullName ?? 'User';
  const initials = initialsOf(name);
  const roleLabel = user?.staffRoleName || user?.role || '';

  function signOut() {
    auth.clear();
    nav('/login');
  }

  async function uploadPhoto(file: File) {
    if (!file.type.startsWith('image/')) { alert('Please choose an image file.'); return; }
    setUploading(true);
    try {
      setProfile(await api.uploadProfilePhoto(file, file.type));
    } catch (e: any) {
      alert(e?.message ?? 'Failed to upload photo.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="user-menu" ref={ref}>
      <button type="button" className="user-menu-trigger" onClick={() => setOpen(o => !o)}
              aria-haspopup="menu" aria-expanded={open}>
        <span className="user-avatar" aria-hidden>{initials}</span>
        <span className="user-menu-id">
          <span className="user-menu-name">{name}</span>
          {roleLabel && <span className="user-menu-role">{roleLabel}</span>}
        </span>
        <span className={'caret' + (open ? ' open' : '')} aria-hidden>▾</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-head">
            <div className="user-menu-name">{name}</div>
            {user?.email && <div className="user-menu-email">{user.email}</div>}
          </div>
          <button type="button" className="user-menu-item" role="menuitem"
                  onClick={() => { setOpen(false); setProfileOpen(true); }}>
            👤 My Profile
          </button>
          <button type="button" className="user-menu-item" role="menuitem"
                  onClick={() => { setOpen(false); nav('/change-password'); }}>
            🔑 Change password
          </button>
          <button type="button" className="user-menu-item danger" role="menuitem" onClick={signOut}>
            ⎋ Sign out
          </button>
        </div>
      )}

      {profileOpen && (
        <Modal title="My Profile" onClose={() => setProfileOpen(false)} width={560}>
          {profileError ? (
            <div className="card error" style={{ marginTop: 12 }}>{profileError}</div>
          ) : !profile ? (
            <div className="muted" style={{ marginTop: 12 }}>Loading…</div>
          ) : (
            <ProfileCard profile={profile} onPhoto={uploadPhoto} uploading={uploading} />
          )}
          <div className="modal-actions">
            <button className="primary" onClick={() => setProfileOpen(false)}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
